// Office global search (Session AE, Item 2). A single bar at the top of every office page that
// searches BOTH the navigation (instant, client-side, via searchNav) and the records (debounced,
// /api/search). Results are grouped by type with a heading; each record links to its detail page,
// and a "Search all [type]" link opens that list with the term pre-filled. Keyboard accessible:
// Cmd/Ctrl+K focuses, arrow keys move through results, Enter selects, Escape closes, click-outside
// closes. Under 2 characters only nav results show (no API call).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type Role, type SearchResults } from "../lib/api";
import { searchNav } from "../lib/navSearch";
import { searchActions } from "../lib/commandActions";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

// Record groups in display order, with the list page each "Search all" link opens.
const RECORD_GROUPS: { key: keyof SearchResults; title: string; listPath: string }[] = [
  { key: "sites", title: "Sites", listPath: "/office/sites" },
  { key: "customers", title: "Customers", listPath: "/office/customers" },
  { key: "lifts", title: "Lifts", listPath: "/office/lifts" },
  { key: "jobs", title: "Service records", listPath: "/office/jobsheets" },
  { key: "defects", title: "Defects", listPath: "/office/defects" },
  { key: "quotes", title: "Quotes", listPath: "/office/quotes" },
  // "prospects" dropped (Session AKF, 18 Aug 2026): the Prospects page is retired from ELE's nav,
  // so a search result linking to it would 404. The backend can still return prospect matches in
  // `records` (untouched); this list simply never renders that group.
  { key: "lolerReports", title: "LOLER reports", listPath: "/office/loler" },
];

interface RenderRow {
  label: string;
  subtitle: string;
  url: string;
}
interface RenderSection {
  key: string;
  title: string;
  listPath?: string;
  rows: RenderRow[];
}

// role (P-1, Session AQT, optional): filters an admin-only nav item out of results for a
// non-admin, so search cannot offer a link that dead-ends. Omitted searches everything.
export function GlobalSearch({ role }: { role?: Role } = {}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [records, setRecords] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  // A FAILED record search is distinct from zero results (Session AEH, FIX-2 B3): a network / server
  // error must not read as "No results". retryNonce re-runs the fetch.
  const [searchError, setSearchError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Session AGZ (Item 3): below md the search renders as an icon-only button; tapping it
  // expands this overlay state -- the input takes the full header width until it is
  // dismissed (close button, Escape, click-outside or selecting a result). Desktop never
  // reads this state: the md: overrides on the container restore the fixed 232px input,
  // and the only setter is a md:hidden button, so the state is unreachable at >= md.
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input the moment the mobile overlay opens (the whole point of the tap).
  useEffect(() => {
    if (mobileExpanded) inputRef.current?.focus();
  }, [mobileExpanded]);

  const trimmed = query.trim();
  const navResults = useMemo(() => searchNav(query, undefined, role), [query, role]);
  // Command-palette actions (Session AAV, Loop 2): instant, client-side, shown first. With an
  // EMPTY query the palette (opened via Cmd/Ctrl+K or focus) shows every action, Linear-style;
  // with a typed query at most three actions match, so page and record results (the search's
  // original muscle memory) are never pushed far down the list.
  const actionResults = useMemo(() => {
    const all = searchActions(query);
    return query.trim().length === 0 ? all : all.slice(0, 3);
  }, [query]);

  // Debounced record search (only at or above the minimum length; never one query per keystroke).
  useEffect(() => {
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setRecords(null);
      setSearchError(false);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    setSearchError(false);
    const t = setTimeout(() => {
      api
        .search(trimmed, 3)
        .then((r) => {
          if (live) {
            setRecords(r);
            setLoading(false);
          }
        })
        .catch(() => {
          if (live) {
            // Keep the nav/action results showing and flag a retryable failure, never the
            // misleading "No results" (Session AEH, FIX-2 B3).
            setRecords(null);
            setSearchError(true);
            setLoading(false);
          }
        });
    }, DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [trimmed, retryNonce]);

  // The rendered sections (actions first, then nav, then each non-empty record group).
  const sections = useMemo<RenderSection[]>(() => {
    const out: RenderSection[] = [];
    if (actionResults.length > 0) {
      out.push({ key: "actions", title: "Actions", rows: actionResults.map((a) => ({ label: a.label, subtitle: a.subtitle, url: a.url })) });
    }
    if (navResults.length > 0) {
      out.push({ key: "nav", title: "Pages", rows: navResults.map((n) => ({ label: n.label, subtitle: n.hub ?? n.section, url: n.to })) });
    }
    if (records) {
      for (const g of RECORD_GROUPS) {
        const items = records[g.key];
        if (items.length > 0) {
          out.push({ key: g.key, title: g.title, listPath: g.listPath, rows: items.map((it) => ({ label: it.label, subtitle: it.subtitle, url: it.url })) });
        }
      }
    }
    return out;
  }, [actionResults, navResults, records]);

  // Flat list of selectable row urls + each section's starting global index (for arrow-key nav).
  const { flatUrls, offsets } = useMemo(() => {
    const urls: string[] = [];
    const offs: number[] = [];
    let acc = 0;
    for (const s of sections) {
      offs.push(acc);
      for (const r of s.rows) urls.push(r.url);
      acc += s.rows.length;
    }
    return { flatUrls: urls, offsets: offs };
  }, [sections]);

  // Reset the keyboard cursor whenever the query changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [trimmed]);

  // Cmd/Ctrl+K focuses the search from anywhere in the office app. Below md the input is
  // hidden until the overlay expands, so the shortcut also expands it (a keyboard-attached
  // tablet under 768px); at >= md the expanded state is inert (every md: override restores
  // the desktop layout), so setting it here is harmless on desktop.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setMobileExpanded(true);
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Click outside closes the dropdown (and the mobile overlay with it).
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setMobileExpanded(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  const select = useCallback(
    (url: string) => {
      setOpen(false);
      setMobileExpanded(false);
      setQuery("");
      setRecords(null);
      setActiveIndex(-1);
      navigate(url);
    },
    [navigate],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      setMobileExpanded(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, flatUrls.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && flatUrls[activeIndex]) {
        e.preventDefault();
        select(flatUrls[activeIndex]!);
      }
    }
  };

  const hasAny = sections.length > 0;
  // Show the dropdown when there is something to show: any results, a 2+ char query (which
  // shows the loading / no-results state), or -- palette behaviour (Session AAV) -- an EMPTY
  // query with the actions list, so Cmd/Ctrl+K opens straight onto the available actions.
  const showDropdown = open && (trimmed.length === 0 ? hasAny : hasAny || trimmed.length >= MIN_QUERY_LENGTH);
  const searchFailed = showDropdown && trimmed.length >= MIN_QUERY_LENGTH && !loading && searchError;
  const noResults = showDropdown && trimmed.length >= MIN_QUERY_LENGTH && !loading && !searchError && !hasAny;

  return (
    // 232px width in the office header (Session ACG locked: 220-240px). On desktop this is
    // unchanged: w-full is always capped by max-w-[232px], so the input renders at exactly
    // 232px wherever the header has room (it always does at >= md). The dropdown is wider
    // than the input (w-[26rem], right-anchored, max-w-[80vw]) so result labels and
    // subtitles stay readable.
    //
    // Session AGZ (Item 3): below md the input is GONE by default -- a 44px icon-only
    // magnifier (below) opens it as a full-width overlay across the header row, freeing the
    // small header for the hamburger and the logo. The overlay positions against the sticky
    // header (position:sticky establishes the containing block); every md: override below
    // restores the desktop layout exactly, so a stray expanded state can never restyle the
    // desktop header.
    <>
      {/* The mobile trigger: a 44px icon-only magnifier in place of the input. md:hidden keeps
          it a phone-only control; it also unmounts while the overlay is open so nothing
          focusable sits underneath the expanded bar. */}
      {!mobileExpanded && (
        <button
          type="button"
          onClick={() => setMobileExpanded(true)}
          aria-label="Open search"
          className="flex h-11 w-11 flex-none items-center justify-center rounded-ele text-ele-body md:hidden"
        >
          <svg aria-hidden viewBox="0 0 20 20" className="h-5 w-5 fill-current">
            <path d="M9 3a6 6 0 014.47 10.03l3.25 3.24-1.42 1.42-3.24-3.25A6 6 0 119 3zm0 2a4 4 0 100 8 4 4 0 000-8z" />
          </svg>
        </button>
      )}
    <div
      ref={containerRef}
      className={
        mobileExpanded
          ? "absolute inset-x-0 top-0 z-30 flex h-full items-center gap-1 bg-[var(--ele-shell)] px-3 md:relative md:inset-auto md:z-auto md:block md:h-auto md:w-full md:min-w-0 md:max-w-[232px] md:bg-transparent md:px-0"
          : "relative w-full min-w-0 max-w-[232px] max-md:hidden"
      }
    >
      {mobileExpanded && (
        <button
          type="button"
          // Symmetric with every other dismiss path (review fix): closing the bar also
          // closes the dropdown state, so a later viewport crossing cannot resurrect a
          // stale results panel.
          onClick={() => {
            setOpen(false);
            setMobileExpanded(false);
          }}
          aria-label="Close search"
          className="flex h-11 w-11 flex-none items-center justify-center rounded-ele text-ele-body md:hidden"
        >
          <svg aria-hidden viewBox="0 0 20 20" className="h-5 w-5 fill-current">
            <path d="M4.7 3.3 3.3 4.7 8.6 10l-5.3 5.3 1.4 1.4L10 11.4l5.3 5.3 1.4-1.4L11.4 10l5.3-5.3-1.4-1.4L10 8.6 4.7 3.3z" />
          </svg>
        </button>
      )}
      <div className={mobileExpanded ? "relative min-w-0 flex-1" : "relative"}>
        <span aria-hidden className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ele-body opacity-60">
          {/* magnifier */}
          <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current">
            <path d="M9 3a6 6 0 014.47 10.03l3.25 3.24-1.42 1.42-3.24-3.25A6 6 0 119 3zm0 2a4 4 0 100 8 4 4 0 000-8z" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search..."
          aria-label="Search pages and records"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="global-search-results"
          aria-autocomplete="list"
          // Announce the arrow-key highlighted option to assistive tech (Session AAV): the
          // option ids below are stable per index.
          aria-activedescendant={showDropdown && activeIndex >= 0 ? `global-search-option-${activeIndex}` : undefined}
          // Suppress the browser's native autocomplete/history dropdown: on a type="search" input it
          // floats in the browser chrome layer ABOVE our results, so the office sees its own past
          // searches (or nothing) and the custom dropdown never shows -- it reads as "the search does
          // not respond" (Session AF, Item 1 Fix 3). autoComplete off + 1Password/LastPass opt-out.
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          className="w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset py-1.5 pl-9 pr-12 text-sm text-ele-strong placeholder:text-ele-body focus:border-ele-purple focus:outline-none"
        />
        {/* Locked Ctrl K hint badge (Session ACG): 10px on a faint chip. The focus ring above is
            full-intensity purple -- one of its three reserved uses. Session AFK: the chip's two
            white washes (0.12 border, 0.06 fill) became the hairline tokens (0.10 / 0.05), the
            nearest exact pair; a raw white wash would be invisible on the light theme's white
            chrome. The 0.02 alpha difference is imperceptible on the dark ground. */}
        <kbd aria-hidden className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded-[3px] border border-[var(--ele-hairline)] bg-[var(--ele-hairline-soft)] px-[5px] py-px font-menu text-[10px] text-ele-muted sm:block">
          Ctrl K
        </kbd>
      </div>

      {showDropdown && (
        <div
          id="global-search-results"
          role="listbox"
          // top-full anchors the dropdown below the container in BOTH layouts (in the
          // mobile overlay the container is a flex row, where an absolute child's static
          // position would no longer be "underneath" -- Session AGZ).
          className="ele-table-scroll absolute right-0 top-full z-30 mt-1 max-h-[70vh] w-[26rem] max-w-[80vw] overflow-y-auto rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] shadow-xl"
        >
          {loading && trimmed.length >= MIN_QUERY_LENGTH && (
            <div className="px-3 py-2 text-xs text-ele-body opacity-70">Searching...</div>
          )}

          {sections.map((section, si) => (
            <div key={section.key} className="border-b border-ele-hairline last:border-0">
              <div className="px-3 pt-2 font-menu text-[10px] uppercase tracking-widest text-ele-blue opacity-70">{section.title}</div>
              <ul className="py-1">
                {section.rows.map((row, j) => {
                  const index = offsets[si]! + j;
                  const active = index === activeIndex;
                  return (
                    <li key={`${section.key}-${row.url}-${j}`} id={`global-search-option-${index}`} role="option" aria-selected={active}>
                      <button
                        type="button"
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => select(row.url)}
                        className={`flex w-full items-baseline justify-between gap-3 px-3 py-1.5 text-left text-sm transition ${
                          active ? "bg-ele-inset-strong text-ele-green" : "text-ele-strong hover:bg-ele-row-hover"
                        }`}
                      >
                        <span className="truncate">{row.label}</span>
                        {row.subtitle && <span className="flex-none truncate text-xs text-ele-body opacity-70">{row.subtitle}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {section.listPath && (
                <button
                  type="button"
                  onClick={() => select(`${section.listPath}?q=${encodeURIComponent(trimmed)}`)}
                  className="block w-full px-3 pb-2 text-left font-menu text-[10px] tracking-wider text-ele-body opacity-60 transition hover:text-ele-green hover:opacity-100"
                >
                  Search all {section.title.toLowerCase()}
                </button>
              )}
            </div>
          ))}

          {noResults && <div className="px-3 py-3 text-sm text-ele-body">No results for &ldquo;{trimmed}&rdquo;.</div>}

          {searchFailed && (
            <div role="alert" className="px-3 py-3 text-sm text-ele-danger">
              Could not search records.{" "}
              <button
                type="button"
                onClick={() => setRetryNonce((n) => n + 1)}
                className="font-menu tracking-wide underline underline-offset-2 transition hover:text-ele-strong"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
}
